import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Trash2, Upload, Plus, FileText, AlertTriangle, CheckCircle2, Eye, X, Download, ExternalLink, Image as ImageIcon } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";
import { useAuth } from "../context/AuthContext";
import { tokenStore, API as API_BASE } from "../lib/api";

const currentYear = () => new Date().getFullYear();

// Estimate local working days excluding weekends (backend recomputes with cantonal holidays)
function estimateWorkingDays(s, e) {
  if (!s || !e) return 0;
  try {
    const d0 = new Date(s);
    const d1 = new Date(e);
    if (isNaN(d0) || isNaN(d1) || d1 < d0) return 0;
    let days = 0;
    const cur = new Date(d0);
    while (cur <= d1) {
      const wd = cur.getDay();
      if (wd !== 0 && wd !== 6) days += 1;
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  } catch { return 0; }
}

export default function MeldeverfahrenTab({ personId, personName, isSubcontractor }) {
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const [summary, setSummary] = useState({ year: currentYear(), limit: 90, used_days: 0, remaining_days: 90, entries: [] });
  const [year, setYear] = useState(currentYear());
  const [loading, setLoading] = useState(false);
  const [allPersons, setAllPersons] = useState([]);

  // Manual add
  const [openAdd, setOpenAdd] = useState(false);
  const [manualForm, setManualForm] = useState({ start_date: "", end_date: "", working_days: "", note: "" });

  // File viewer
  const [viewingFile, setViewingFile] = useState(null); // {mid, filename, content_type}

  const meldungFileUrl = (mid) => {
    const t = tokenStore.get() || "";
    return `${API_BASE}/persons/${personId}/meldungen/${mid}/file?token=${encodeURIComponent(t)}`;
  };

  const downloadFile = async (mid, filename) => {
    try {
      const res = await api.get(`/persons/${personId}/meldungen/${mid}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `meldung-${mid}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Eroare la descărcare");
    }
  };

  // Upload + multi-row preview
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // raw response from /preview-upload (file metadata + entries[])
  const [previewRows, setPreviewRows] = useState([]); // editable copy of entries (with __skip flag)

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get(`/persons/${personId}/meldungen`, { params: { year } }),
        api.get("/persons-overview").catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setAllPersons(p.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la încărcare Meldungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (personId) load(); }, [personId, year]);

  const remaining = summary.remaining_days ?? 90;
  const used = summary.used_days ?? 0;
  const pct = Math.min(100, Math.round((used / 90) * 100));
  const low = remaining < 10;

  const personOptions = useMemo(() => {
    return (allPersons || [])
      .filter((p) => p.active !== false)
      .map((p) => ({
        id: p.id,
        label: `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)",
        is_sub: !!p.is_subcontractor,
      }))
      .sort((a, b) => {
        if (a.is_sub !== b.is_sub) return a.is_sub ? -1 : 1; // subcontractors first
        return a.label.localeCompare(b.label, "ro");
      });
  }, [allPersons]);

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/persons/${personId}/meldungen/preview-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const rows = (data.entries || []).map((en, idx) => ({
        __key: `${idx}`,
        __skip: false,
        person_id: en.matched_person_id || (idx === 0 ? personId : null),
        matched_person_name: en.matched_person_name,
        employee_name: en.employee_name || "",
        start_date: en.start_date || "",
        end_date: en.end_date || "",
        working_days: en.suggested_working_days || 0,
        note: "",
      }));
      // Dacă OCR-ul n-a returnat nimic, dă utilizatorului un rând gol pre-completat cu persoana curentă
      if (rows.length === 0) {
        rows.push({
          __key: "0",
          __skip: false,
          person_id: personId,
          matched_person_name: personName,
          employee_name: personName,
          start_date: "",
          end_date: "",
          working_days: 0,
          note: "",
        });
      }
      setPreviewRows(rows);
      if (data.extract_error) {
        toast.warning("OCR eșuat. Completează rândurile manual.");
      } else if ((data.entries || []).length === 0) {
        toast.warning("Niciun angajat detectat automat. Completează manual.");
      } else {
        const matched = (data.entries || []).filter((en) => en.matched_person_id).length;
        toast.success(`Detectați ${data.entries.length} angajați (${matched} potriviți automat). Verifică și salvează.`);
      }
    } catch (err) {
      const d = err?.response?.data;
      const msg = (d && (d.detail || d.message)) || err?.message || "Eroare la upload";
      toast.error(`Eroare la upload: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  const updateRow = (key, field, value) => {
    setPreviewRows((rows) => rows.map((r) => {
      if (r.__key !== key) return r;
      const next = { ...r, [field]: value };
      if (field === "start_date" || field === "end_date") {
        next.working_days = estimateWorkingDays(field === "start_date" ? value : next.start_date, field === "end_date" ? value : next.end_date);
      }
      return next;
    }));
  };

  const toggleSkip = (key) => setPreviewRows((rows) => rows.map((r) => r.__key === key ? { ...r, __skip: !r.__skip } : r));

  const addEmptyRow = () => {
    setPreviewRows((rows) => [
      ...rows,
      { __key: `${Date.now()}-${rows.length}`, __skip: false, person_id: null, matched_person_name: null, employee_name: "", start_date: "", end_date: "", working_days: 0, note: "" },
    ]);
  };

  const cancelPreview = async () => {
    if (preview?.file_id) {
      try { await api.delete(`/persons/${personId}/meldungen/preview/${preview.file_id}`); } catch {}
    }
    setPreview(null);
    setPreviewRows([]);
  };

  const confirmPreview = async () => {
    const valid = previewRows.filter((r) => !r.__skip);
    const invalid = valid.filter((r) => !r.person_id || !r.start_date || !r.end_date);
    if (valid.length === 0) { toast.error("Toate rândurile sunt marcate skip"); return; }
    if (invalid.length > 0) {
      toast.error(`${invalid.length} rând(uri) au date incomplete (persoană / start / end). Bifează skip sau completează.`);
      return;
    }
    try {
      const body = {
        file_id: preview.file_id,
        storage_path: preview.storage_path,
        original_filename: preview.original_filename,
        content_type: preview.content_type,
        size: preview.size,
        extracted: preview.extracted,
        entries: valid.map((r) => ({
          person_id: r.person_id,
          employee_name: r.employee_name || null,
          start_date: r.start_date,
          end_date: r.end_date,
          working_days: Number(r.working_days) || null,
          note: r.note || null,
        })),
      };
      const { data } = await api.post("/meldungen/bulk-confirm", body);
      toast.success(`Salvate ${data.created} intrări${data.skipped ? ` (sărite: ${data.skipped})` : ""}`);
      setPreview(null);
      setPreviewRows([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    }
  };

  const addManual = async () => {
    if (!manualForm.start_date || !manualForm.end_date) { toast.error("Start și End sunt obligatorii"); return; }
    try {
      await api.post(`/persons/${personId}/meldungen`, {
        start_date: manualForm.start_date,
        end_date: manualForm.end_date,
        working_days: manualForm.working_days ? Number(manualForm.working_days) : null,
        note: manualForm.note || null,
      });
      toast.success("Adăugat manual");
      setOpenAdd(false);
      setManualForm({ start_date: "", end_date: "", working_days: "", note: "" });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare"); }
  };

  const onDelete = async (mid) => {
    if (!(await confirm({ body: "Ștergi această înregistrare Meldung?", danger: true }))) return;
    try {
      await api.delete(`/persons/${personId}/meldungen/${mid}`);
      toast.success("Șters");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare"); }
  };

  if (!isSubcontractor) {
    return (
      <div className="card-flat p-6 text-sm text-zinc-600" data-testid="meldung-not-subcontractor">
        Această secțiune este disponibilă doar pentru angajații marcați ca <b>Subcontractor</b>.
        Bifează „Subcontractor (Meldeverfahren)" în tab-ul <b>Date</b> (sau selectează o firmă diferită de „ConVic" — flag-ul se setează automat).
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="meldeverfahren-tab">
      {/* Summary */}
      <div className={`card-flat p-5 ${low ? "border-2 border-red-300 bg-red-50" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm text-zinc-500">Meldeverfahren {summary.year} — limită legală</div>
            <div className="text-2xl font-display font-bold mt-1 inline-flex items-center gap-2">
              <span className={low ? "text-red-700" : "text-zinc-900"}>{remaining}</span>
              <span className="text-zinc-400 text-lg">/ 90 zile lucrătoare rămase</span>
              {low && <AlertTriangle className="text-red-600" size={22} />}
            </div>
            <div className="text-xs text-zinc-600 mt-1">
              Folosite: <b>{used}</b> zile · {personName}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">An:</Label>
            <Input
              type="number" min={2020} max={2100}
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value) || currentYear())}
              className="w-24"
              data-testid="meldung-year-input"
            />
          </div>
        </div>
        <div className="w-full h-2 rounded-full bg-zinc-200 mt-3 overflow-hidden">
          <div className={`h-full transition-all ${pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
        </div>
        {low && (
          <div className="mt-3 text-sm text-red-700 font-medium inline-flex items-center gap-2" data-testid="meldung-low-alert">
            <AlertTriangle size={16} /> Atenție: au mai rămas mai puțin de 10 zile lucrătoare până la limita anuală.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={onFileChange} data-testid="meldung-file-input" />
        <Button onClick={onPickFile} disabled={uploading} data-testid="meldung-upload-btn">
          <Upload size={16} className="mr-1" />
          {uploading ? "Procesez OCR…" : "Încarcă Meldung (PDF/imagine)"}
        </Button>
        <Button variant="outline" onClick={() => setOpenAdd(true)} data-testid="meldung-add-manual-btn">
          <Plus size={16} className="mr-1" /> Adaugă manual
        </Button>
        <p className="text-xs text-zinc-500 ml-2">Un document poate conține mai mulți angajați — la upload, OCR-ul detectează tot și le distribuie automat.</p>
      </div>

      {/* Entries list */}
      <div className="card-flat p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--border)] text-sm font-semibold flex items-center justify-between">
          <span>📜 Istoric Meldungen ({summary.entries?.length || 0})</span>
          <span className="text-xs font-normal text-zinc-500">Cele mai recente sus</span>
        </div>
        {loading && <div className="p-4 text-sm text-zinc-500">Se încarcă…</div>}
        {!loading && (!summary.entries || summary.entries.length === 0) && (
          <div className="p-4 text-sm text-zinc-500" data-testid="meldung-empty">
            Nicio înregistrare. Încarcă un document Meldung sau adaugă manual.
          </div>
        )}
        {!loading && summary.entries && summary.entries.length > 0 && (
          <div className="divide-y divide-[var(--border)]">
            {summary.entries.map((m) => {
              const isImg = (m.content_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(m.original_filename || "");
              const isPdf = (m.content_type || "").includes("pdf") || /\.pdf$/i.test(m.original_filename || "");
              const yr = (m.start_date || "").slice(0, 4) || "—";
              return (
                <div key={m.id} className="p-3 flex flex-wrap items-start gap-3 hover:bg-zinc-50/60" data-testid={`meldung-row-${m.id}`}>
                  <div className="flex-1 min-w-[240px]">
                    <div className="text-sm font-medium inline-flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-bold">{yr}</span>
                      <span className="font-display">{fmtRoDate(m.start_date)} <span className="text-zinc-400">→</span> {fmtRoDate(m.end_date)}</span>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">{m.working_days} zile</span>
                      {m.document_batch_id && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200" title="Provenit dintr-un document cu mai mulți angajați">
                          📑 lot
                        </span>
                      )}
                    </div>
                    {m.employee_name_extracted && (
                      <div className="text-xs text-zinc-500 mt-1">
                        <span className="text-zinc-400">Nume extras din document:</span> <span className="font-medium">{m.employee_name_extracted}</span>
                      </div>
                    )}
                    {m.note && <div className="text-xs text-zinc-700 mt-1">📝 {m.note}</div>}
                    {m.original_filename && (
                      <div className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1">
                        {isImg ? <ImageIcon size={12} className="text-purple-600" /> : <FileText size={12} className={isPdf ? "text-red-600" : "text-blue-600"} />}
                        <span className="font-mono truncate max-w-[260px]">{m.original_filename}</span>
                      </div>
                    )}
                    {m.created_at && (
                      <div className="text-[10px] text-zinc-400 mt-1">
                        Salvat de {m.created_by || "—"} la {fmtRoDate(m.created_at.slice(0, 10))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {m.file_id && (
                      <>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setViewingFile({ mid: m.id, filename: m.original_filename, content_type: m.content_type, isImg, isPdf })}
                          className="text-blue-700 border-blue-300 hover:bg-blue-50"
                          data-testid={`meldung-view-${m.id}`}
                          title="Previzualizează documentul"
                        >
                          <Eye size={14} className="mr-1" /> Vezi
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => downloadFile(m.id, m.original_filename)}
                          className="text-zinc-600 hover:text-emerald-700"
                          data-testid={`meldung-download-${m.id}`}
                          title="Descarcă fișierul"
                        >
                          <Download size={14} />
                        </Button>
                      </>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="ghost" onClick={() => onDelete(m.id)} className="text-red-600 hover:text-red-700" data-testid={`meldung-delete-${m.id}`} title="Șterge">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* File viewer dialog */}
      <Dialog open={!!viewingFile} onOpenChange={(o) => { if (!o) setViewingFile(null); }}>
        <DialogContent className="max-w-5xl" data-testid="meldung-file-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 truncate pr-8">
              {viewingFile?.isImg ? <ImageIcon size={16}/> : <FileText size={16}/>}
              <span className="truncate">{viewingFile?.filename || "Meldung"}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="bg-zinc-100 rounded-md overflow-hidden flex items-center justify-center" style={{ minHeight: "60vh", maxHeight: "75vh" }}>
            {viewingFile?.isImg && (
              <img src={meldungFileUrl(viewingFile.mid)} alt={viewingFile.filename} className="max-w-full max-h-[75vh] object-contain" />
            )}
            {viewingFile?.isPdf && (
              <iframe src={meldungFileUrl(viewingFile.mid)} title={viewingFile.filename} className="w-full" style={{ minHeight: "70vh" }} />
            )}
            {viewingFile && !viewingFile.isImg && !viewingFile.isPdf && (
              <div className="text-center p-8">
                <FileText size={56} className="mx-auto text-zinc-400" />
                <div className="text-sm text-zinc-600 mt-3">Previzualizare indisponibilă pentru acest tip de fișier.</div>
                <a href={meldungFileUrl(viewingFile.mid)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[var(--primary)] mt-3 hover:underline">
                  <ExternalLink size={14}/> Deschide într-un tab nou
                </a>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between mt-3">
            {viewingFile && (
              <a href={meldungFileUrl(viewingFile.mid)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-[var(--primary)]">
                <ExternalLink size={14}/> Deschide într-un tab nou
              </a>
            )}
            {viewingFile && (
              <Button onClick={() => downloadFile(viewingFile.mid, viewingFile.filename)} data-testid="meldung-viewer-download" className="gap-1.5">
                <Download size={14}/> Descarcă
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Multi-row preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) cancelPreview(); }}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="meldung-preview-dialog">
          <DialogHeader>
            <DialogTitle>Confirmă datele extrase din document</DialogTitle>
            <p className="text-xs text-zinc-500 mt-1">
              OCR-ul a detectat angajații și intervalele de mai jos. Verifică, corectează și ALEGE PERSOANA pentru fiecare rând.
              Numărul de zile se recalculează automat (excluzând weekend + sărbători cantonale).
              📄 {preview?.original_filename || ""}
            </p>
          </DialogHeader>

          {preview?.extract_error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-xs inline-flex items-center gap-2 mb-3" data-testid="meldung-extract-error">
              <AlertTriangle size={14} /> OCR eșuat: {preview.extract_error}. Completează rândurile manual.
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="meldung-preview-table">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="text-left px-2 py-2 w-8">#</th>
                  <th className="text-left px-2 py-2">Nume extras (din document)</th>
                  <th className="text-left px-2 py-2 min-w-[220px]">Persoană DB</th>
                  <th className="text-left px-2 py-2">Start</th>
                  <th className="text-left px-2 py-2">End</th>
                  <th className="text-left px-2 py-2">Zile</th>
                  <th className="text-left px-2 py-2">Notă</th>
                  <th className="text-left px-2 py-2 w-12">Skip</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {previewRows.map((r, idx) => {
                  const skipped = r.__skip;
                  const noMatch = !r.person_id;
                  return (
                    <tr key={r.__key} className={`${skipped ? "opacity-40 bg-zinc-50" : ""} ${noMatch && !skipped ? "bg-amber-50" : ""}`} data-testid={`meldung-preview-row-${idx}`}>
                      <td className="px-2 py-2 align-top text-zinc-500">{idx + 1}</td>
                      <td className="px-2 py-2 align-top">
                        <Input value={r.employee_name} onChange={(e) => updateRow(r.__key, "employee_name", e.target.value)} disabled={skipped} className="h-8" data-testid={`meldung-preview-name-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Select value={r.person_id || "__none__"} onValueChange={(v) => updateRow(r.__key, "person_id", v === "__none__" ? null : v)} disabled={skipped}>
                          <SelectTrigger className="h-8" data-testid={`meldung-preview-person-${idx}`}>
                            <SelectValue placeholder={noMatch ? "⚠ Selectează persoana" : ""} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Niciuna (skip) —</SelectItem>
                            {personOptions.map((p) => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.is_sub ? "🌍 " : ""}{p.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {r.matched_person_name && r.person_id && (
                          <div className="text-[10px] text-emerald-700 mt-0.5">✓ Auto-potrivit</div>
                        )}
                        {noMatch && !skipped && (
                          <div className="text-[10px] text-amber-700 mt-0.5">⚠ Niciun match — alege manual sau skip</div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input type="date" value={r.start_date} onChange={(e) => updateRow(r.__key, "start_date", e.target.value)} disabled={skipped} className="h-8 w-[140px]" data-testid={`meldung-preview-start-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input type="date" value={r.end_date} onChange={(e) => updateRow(r.__key, "end_date", e.target.value)} disabled={skipped} className="h-8 w-[140px]" data-testid={`meldung-preview-end-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input type="number" min={0} value={r.working_days} onChange={(e) => updateRow(r.__key, "working_days", e.target.value)} disabled={skipped} className="h-8 w-[70px]" data-testid={`meldung-preview-days-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top">
                        <Input value={r.note} onChange={(e) => updateRow(r.__key, "note", e.target.value)} disabled={skipped} className="h-8" placeholder="opțional" data-testid={`meldung-preview-note-${idx}`} />
                      </td>
                      <td className="px-2 py-2 align-top text-center">
                        <Button size="sm" variant="ghost" onClick={() => toggleSkip(r.__key)} className={skipped ? "text-zinc-500" : "text-red-600 hover:text-red-700"} data-testid={`meldung-preview-skip-${idx}`} title={skipped ? "Anulează skip" : "Sari peste acest rând"}>
                          <X size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-3">
            <Button variant="outline" size="sm" onClick={addEmptyRow} data-testid="meldung-preview-add-row">
              <Plus size={14} className="mr-1" /> Adaugă rând
            </Button>
            <div className="text-xs text-zinc-500">
              {previewRows.filter((r) => !r.__skip && r.person_id && r.start_date && r.end_date).length} rând(uri) valide /
              {" "}{previewRows.length} total
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={cancelPreview} data-testid="meldung-preview-cancel">Anulează</Button>
            <Button onClick={confirmPreview} data-testid="meldung-preview-confirm">Salvează toate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual add dialog */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent data-testid="meldung-manual-dialog">
          <DialogHeader>
            <DialogTitle>Adaugă Meldung manual</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Start<span className="text-red-600">*</span></Label><Input type="date" value={manualForm.start_date} onChange={(e) => setManualForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="meldung-manual-start" /></div>
            <div><Label>End<span className="text-red-600">*</span></Label><Input type="date" value={manualForm.end_date} onChange={(e) => setManualForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="meldung-manual-end" /></div>
            <div><Label>Zile lucrătoare (lasă gol pentru auto)</Label><Input type="number" min={0} value={manualForm.working_days} onChange={(e) => setManualForm((f) => ({ ...f, working_days: e.target.value }))} data-testid="meldung-manual-days" /></div>
            <div className="md:col-span-2"><Label>Notă</Label><Textarea rows={2} value={manualForm.note} onChange={(e) => setManualForm((f) => ({ ...f, note: e.target.value }))} data-testid="meldung-manual-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAdd(false)}>Anulează</Button>
            <Button onClick={addManual} data-testid="meldung-manual-save">Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
